import React, { useEffect, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { Box, CssBaseline } from '@mui/material';
import LoadingState from '../components/LoadingState';

const UserLayout: React.FC = () => {
  const auth = useUser();
  const { logout, user, isLoading, isAdmin, clients } = auth;
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Redirects based on user state. With cookie auth, the only states are:
  //   - isLoading: UserContext is calling /api/auth/session
  //   - !isLoading && user: authenticated — apply routing rules
  //   - !isLoading && !user: not authenticated — send to landing
  useEffect(() => {
    if (isLoading) return;

    // These are automatic routing corrections: don't add a history entry
    // that Back would revisit only to redirect again, or navigate in place.
    const redirect = (target: string) => {
      if (location.pathname !== target) navigate(target, { replace: true });
    };

    if (!user) {
      // Only redirect to landing if we're inside the /app shell. Public
      // routes (/, /login, /signup, etc.) don't render UserLayout at all.
      redirect('/');
      return;
    }

    const isAdminRoute = location.pathname.startsWith('/app/admin');
    const isWelcomeRoute = location.pathname === '/app/welcome';
    const isRootRoute = location.pathname === '/app';
    const userIsAdmin = isAdmin();
    const userIsToolsOnly = !!user.is_tools_user && !userIsAdmin;
    const hasClients = clients.length > 0;

    if (isAdminRoute && !userIsAdmin && !userIsToolsOnly) {
      redirect('/app/welcome');
      return;
    }

    // Tools-only users sit above the per-client world: skip the welcome
    // invitation-code prompt and land them on their primary tool page.
    if (userIsToolsOnly && (isRootRoute || isWelcomeRoute)) {
      redirect('/app/admin/qrcodes');
      return;
    }

    if (!hasClients && !isWelcomeRoute && !userIsToolsOnly) {
      redirect('/app/welcome');
      return;
    }

    if ((isRootRoute || isWelcomeRoute) && hasClients && !location.search) {
      redirect('/app');
      return;
    }
  }, [user, isLoading, clients, isAdmin, location.pathname, location.search, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: '100%',
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <LoadingState message="Authenticating..." variant="page" size="medium" />
        </Box>
      </Box>
    );
  }

  if (!user) {
    // The redirect effect above will navigate to '/' shortly; show a spinner
    // in the meantime so the layout doesn't render a broken shell.
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: '100%',
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <LoadingState message="Redirecting..." variant="page" size="medium" />
        </Box>
      </Box>
    );
  }

  const handleLogout = () => {
    logout();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <TopBar onLogout={handleLogout} />
      <Sidebar onToggle={(open) => setSidebarOpen(open)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarOpen ? 240 : 64}px)` },
          ml: { sm: `${sidebarOpen ? 240 : 64}px` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'background.default',
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          position: 'relative',
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default UserLayout;
